import React, { useMemo, useState, useCallback, useRef } from 'react';
import { 
  StackedBarChartWithGroupsProps, 
  ThemeConfig, 
  PresetTheme,
  BarClickData,
  ChartDimensions,
} from '../types';
import { ChartContainer, Axis, Bar, Legend, Tooltip, Title } from './core';
import { 
  calculateScale, 
  getNumericValue, 
  valueToPosition,
} from '../utils/scales';
import { 
  getActiveTheme, 
  mergeAxisConfig, 
  mergeLegendConfig, 
  mergeTooltipConfig, 
  mergeAnimationConfig,
} from '../utils/defaults';
import { getDatasetColor, getHoverColor, resolveColor } from '../utils/colors';
import { getStackedBorderRadius } from '../utils/svg';

export interface StackedBarChartWithGroupsComponentProps extends StackedBarChartWithGroupsProps {
  /** Preset theme name */
  theme?: PresetTheme;
  /** Custom theme configuration */
  themeConfig?: ThemeConfig;
}

/**
 * StackedBarChartWithGroups Component
 * 
 * A stacked bar chart that supports multiple stack groups side by side.
 * Use the `stack` property on each dataset to assign them to different groups.
 * Fully responsive with mobile-optimized rendering.
 * 
 * @example
 * ```tsx
 * <StackedBarChartWithGroups
 *   labels={['Q1', 'Q2', 'Q3', 'Q4']}
 *   datasets={[
 *     { label: '2022 Online', data: [50, 60, 70, 80], stack: 'Stack 0' },
 *     { label: '2022 Store', data: [30, 40, 35, 45], stack: 'Stack 0' },
 *     { label: '2023 Online', data: [60, 75, 85, 95], stack: 'Stack 1' },
 *     { label: '2023 Store', data: [35, 45, 40, 50], stack: 'Stack 1' }
 *   ]}
 * />
 * ```
 */
export const StackedBarChartWithGroups: React.FC<StackedBarChartWithGroupsComponentProps> = ({
  labels,
  datasets,
  title,
  xAxis,
  yAxis,
  legend,
  tooltip,
  animation,
  width,
  height = 300,
  padding,
  backgroundColor,
  onBarClick,
  onBarHover,
  className,
  style,
  id,
  ariaLabel,
  borderRadius = 0,
  orientation = 'vertical',
  groupGap = 0.2,
  stackGap = 4,
  showValues,
  valueLabels,
  theme = 'default',
  themeConfig,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [tooltipData, setTooltipData] = useState<{
    x: number;
    y: number;
    value: number | [number, number];
    label: string;
    datasetLabel: string;
    color: string;
    mouseX?: number;
    mouseY?: number;
  } | null>(null);
  const [hiddenDatasets, setHiddenDatasets] = useState<Set<number>>(new Set());

  // Get theme
  const activeTheme = useMemo(() => {
    return getActiveTheme(theme, themeConfig);
  }, [theme, themeConfig]);

  const colors = activeTheme.colors || [];

  // Process datasets to identify stacks
  const { visibleDatasets, stackGroups } = useMemo(() => {
    const visible = datasets.map((ds, i) => ({
      ...ds,
      hidden: ds.hidden || hiddenDatasets.has(i),
      stack: ds.stack || `Stack ${i % 2}`, // Default alternating stacks
    }));
    
    // Group by stack
    const groups = new Map<string, number[]>();
    visible.forEach((ds, i) => {
      if (!ds.hidden) {
        const existing = groups.get(ds.stack!) || [];
        existing.push(i);
        groups.set(ds.stack!, existing);
      }
    });
    
    return { visibleDatasets: visible, stackGroups: groups };
  }, [datasets, hiddenDatasets]);

  // Merge configs
  const xAxisConfig = useMemo(() => mergeAxisConfig(xAxis, undefined, activeTheme), [xAxis, activeTheme]);
  const yAxisConfig = useMemo(() => mergeAxisConfig(yAxis, undefined, activeTheme), [yAxis, activeTheme]);
  const legendConfig = useMemo(() => mergeLegendConfig(legend, undefined, activeTheme), [legend, activeTheme]);
  const tooltipConfig = useMemo(() => mergeTooltipConfig(tooltip, undefined, activeTheme), [tooltip, activeTheme]);
  const animationConfig = useMemo(() => mergeAnimationConfig(animation), [animation]);

  // Calculate scale for all stacked data
  const scale = useMemo(() => {
    // Calculate max for each stack group
    const numPoints = labels.length;
    let dataMin = 0;
    let dataMax = 0;

    stackGroups.forEach((datasetIndices) => {
      for (let i = 0; i < numPoints; i++) {
        let positiveSum = 0;
        let negativeSum = 0;
        
        datasetIndices.forEach(dsIndex => {
          const value = getNumericValue(visibleDatasets[dsIndex].data[i]);
          if (value >= 0) positiveSum += value;
          else negativeSum += value;
        });
        
        dataMax = Math.max(dataMax, positiveSum);
        dataMin = Math.min(dataMin, negativeSum);
      }
    });

    return calculateScale([], {
      min: Math.min(dataMin, yAxis?.min ?? dataMin),
      max: Math.max(dataMax, yAxis?.max ?? dataMax),
      beginAtZero: true,
      maxTicks: 8,
    });
  }, [visibleDatasets, stackGroups, labels.length, yAxis]);

  // Handle bar hover with exact cursor position
  const handleBarHover = useCallback((
    e: React.MouseEvent, 
    data: BarClickData | null,
    mousePos?: { x: number; y: number }
  ) => {
    if (data) {
      setTooltipData({
        x: 0,
        y: 0,
        value: data.value as number,
        label: data.label,
        datasetLabel: data.datasetLabel,
        color: resolveColor(
          datasets[data.datasetIndex].backgroundColor,
          data.dataIndex,
          getNumericValue(data.value),
          getDatasetColor(data.datasetIndex, colors)
        ),
        mouseX: mousePos?.x,
        mouseY: mousePos?.y,
      });
    } else {
      setTooltipData(null);
    }
    onBarHover?.(e, data);
  }, [datasets, colors, onBarHover]);

  const handleToggleDataset = useCallback((index: number) => {
    setHiddenDatasets(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  // Generate accessible description
  const chartDescription = useMemo(() => {
    const groupNames = Array.from(stackGroups.keys()).join(', ');
    return `Stacked bar chart with ${stackGroups.size} groups (${groupNames}) across ${labels.length} categories`;
  }, [stackGroups, labels]);

  const containerWrapperRef = useRef<HTMLDivElement>(null);
  const [chartDimensions, setChartDimensions] = useState<ChartDimensions | null>(null);

  return (
    <div ref={containerWrapperRef} style={{ position: 'relative' }}>
      <ChartContainer
        width={width}
        height={height}
        padding={padding}
        backgroundColor={backgroundColor || activeTheme.backgroundColor}
        className={className}
        style={style}
        id={id}
        ariaLabel={ariaLabel || chartDescription}
        title={typeof title === 'string' ? title : title?.text}
      >
        {(dimensions, containerSvgRef, isMobile) => {
          (svgRef as any).current = containerSvgRef.current;
          // Store dimensions for tooltip
          if (!chartDimensions || chartDimensions.width !== dimensions.width) {
            setTimeout(() => setChartDimensions(dimensions), 0);
          }
        
        const { chartWidth, chartHeight } = dimensions;
        const isVertical = orientation === 'vertical';
        
        // Responsive gaps
        const responsiveGroupGap = isMobile ? Math.max(0.15, groupGap * 0.8) : groupGap;
        const responsiveStackGap = isMobile ? Math.max(2, stackGap * 0.6) : stackGap;
        
        // Calculate dimensions
        const numGroups = labels.length;
        const numStacks = stackGroups.size;
        const categorySize = isVertical ? chartWidth / numGroups : chartHeight / numGroups;
        const groupPadding = categorySize * responsiveGroupGap;
        const availableGroupSize = categorySize - groupPadding;
        const stackWidth = (availableGroupSize - responsiveStackGap * (numStacks - 1)) / numStacks;

        // Render bars
        const bars: React.ReactNode[] = [];
        const stackNames = Array.from(stackGroups.keys());
        
        labels.forEach((label, dataIndex) => {
          stackNames.forEach((stackName, stackIndex) => {
            const datasetIndices = stackGroups.get(stackName) || [];
            let positiveStack = 0;
            let negativeStack = 0;

            // Separate positive and negative for this stack
            const positiveItems = datasetIndices.filter(i => 
              getNumericValue(visibleDatasets[i].data[dataIndex]) >= 0
            );
            const negativeItems = datasetIndices.filter(i => 
              getNumericValue(visibleDatasets[i].data[dataIndex]) < 0
            );

            datasetIndices.forEach((datasetIndex) => {
              const dataset = visibleDatasets[datasetIndex];
              const value = dataset.data[dataIndex];
              const numValue = getNumericValue(value);
              const isPositive = numValue >= 0;

              // Determine position in stack
              const stackItems = isPositive ? positiveItems : negativeItems;
              const posInStack = stackItems.indexOf(datasetIndex);
              const isFirst = posInStack === 0;
              const isLast = posInStack === stackItems.length - 1;

              // Calculate position
              const categoryStart = dataIndex * categorySize + groupPadding / 2;
              const stackOffset = stackIndex * (stackWidth + responsiveStackGap);
              
              let x: number, y: number, barWidth: number, barHeight: number;
              
              if (isVertical) {
                x = categoryStart + stackOffset;
                barWidth = stackWidth;
                
                if (isPositive) {
                  const startPos = valueToPosition(positiveStack, scale, chartHeight, true);
                  const endPos = valueToPosition(positiveStack + numValue, scale, chartHeight, true);
                  y = endPos;
                  barHeight = startPos - endPos;
                  positiveStack += numValue;
                } else {
                  const startPos = valueToPosition(negativeStack, scale, chartHeight, true);
                  const endPos = valueToPosition(negativeStack + numValue, scale, chartHeight, true);
                  y = startPos;
                  barHeight = endPos - startPos;
                  negativeStack += numValue;
                }
              } else {
                y = categoryStart + stackOffset;
                barHeight = stackWidth;
                
                if (isPositive) {
                  const startPos = valueToPosition(positiveStack, scale, chartWidth, false);
                  const endPos = valueToPosition(positiveStack + numValue, scale, chartWidth, false);
                  x = startPos;
                  barWidth = endPos - startPos;
                  positiveStack += numValue;
                } else {
                  const startPos = valueToPosition(negativeStack, scale, chartWidth, false);
                  const endPos = valueToPosition(negativeStack + numValue, scale, chartWidth, false);
                  x = endPos;
                  barWidth = startPos - endPos;
                  negativeStack += numValue;
                }
              }

              // Get colors
              const fillColor = resolveColor(
                dataset.backgroundColor,
                dataIndex,
                numValue,
                getDatasetColor(datasetIndex, colors)
              );
              const strokeColor = resolveColor(dataset.borderColor, dataIndex, numValue, fillColor);
              const hoverFillColor = resolveColor(
                dataset.hoverBackgroundColor,
                dataIndex,
                numValue,
                getHoverColor(fillColor)
              );

              // Get stacked border radius (responsive)
              const baseRadius = dataset.borderRadius ?? borderRadius ?? activeTheme.borderRadius ?? 0;
              const radius = isMobile && typeof baseRadius === 'number' 
                ? Math.max(2, baseRadius * 0.6) 
                : baseRadius;
              const stackedRadius = getStackedBorderRadius(radius, isFirst, isLast, orientation, isPositive);

              bars.push(
                <Bar
                  key={`${datasetIndex}-${dataIndex}`}
                  x={x}
                  y={y}
                  width={barWidth}
                  height={barHeight}
                  fill={fillColor}
                  stroke={strokeColor}
                  strokeWidth={dataset.borderWidth || 0}
                  borderRadius={stackedRadius}
                  opacity={dataset.opacity}
                  animation={animationConfig}
                  animationDelay={(animationConfig.staggerDelay || 0) * (datasetIndex * labels.length + dataIndex)}
                  datasetIndex={datasetIndex}
                  dataIndex={dataIndex}
                  value={value}
                  label={label}
                  datasetLabel={dataset.label}
                  onClick={onBarClick}
                  onHover={handleBarHover}
                  hoverFill={hoverFillColor}
                  isMobile={isMobile}
                />
              );
            });
          });
        });

        return (
          <>
            <Title 
              config={title} 
              dimensions={dimensions}
              defaultColor={activeTheme.textColor}
              defaultFontFamily={activeTheme.fontFamily}
            />

            <Axis
              type="x"
              config={isVertical ? xAxisConfig : { ...xAxisConfig, ...yAxisConfig }}
              scale={scale}
              dimensions={dimensions}
              labels={isVertical ? labels : undefined}
              orientation={orientation}
              isMobile={isMobile}
            />
            <Axis
              type="y"
              config={isVertical ? yAxisConfig : xAxisConfig}
              scale={scale}
              dimensions={dimensions}
              labels={!isVertical ? labels : undefined}
              orientation={orientation}
              isMobile={isMobile}
            />

            <g className="bars" role="list" aria-label="Grouped stacked data bars">
              {bars}
            </g>

            <Legend
              config={legendConfig}
              datasets={visibleDatasets}
              colors={colors}
              dimensions={dimensions}
              onToggleDataset={handleToggleDataset}
              isMobile={isMobile}
            />

          </>
        );
      }}
    </ChartContainer>
      
      {/* Tooltip rendered outside SVG for proper positioning */}
      {tooltipConfig.enabled && tooltipData && chartDimensions && (
        <Tooltip
          config={tooltipConfig}
          data={tooltipData}
          dimensions={chartDimensions}
          containerRef={svgRef}
        />
      )}
    </div>
  );
};

export default StackedBarChartWithGroups;
